import React, { createContext, useContext, useState, useEffect } from "react";
const CryptoJS = require("crypto-js");

/**
 * Contexto do Carrinho do Vendedor
 * 
 * Este contexto gerencia os dados do carrinho que o vendedor compartilha com o usuário.
 * Todas as informações são automaticamente criptografadas e salvas no localStorage.
 * 
 * Funcionalidades:
 * - Salva dados completos do carrinho (produtos, endereço, frete, etc.)
 * - Criptografia automática dos dados no localStorage
 * - Funções para atualizar partes específicas do carrinho
 * - Função para limpar todos os dados
 * 
 * Como usar:
 * 1. Importe o hook: import { useSellerCart } from '../../hooks/useSellerCart';
 * 2. Use no componente: const sellerCartContext = useSellerCart();
 * 3. Acesse os dados: sellerCartContext.data
 * 4. Atualize dados: sellerCartContext.setSellerCartData(newData)
 * 5. Limpe dados: sellerCartContext.clearSellerCart()
 */

type ProductType = {
    id: number;
    image: string;
    code: string;
    locationId: number;
    name: string;
    promotionalPrice: number;
    quantity: number;
    sku: string;
    stock: number;
    unitPrice: number;
    variation: any;
    pluginspaceHash: string;
    outStock: boolean;
    measures?: {
        width: number;
        height: number;
        lenght: number;
        weight: number;
    };
};

type AddressType = {
    address: string;
    cep: string;
    city: string;
    complement: string;
    district: string;
    name: string;
    number: string;
    reference: string;
    telephone: string;
    state: string;
    ibge: string;
};

type JsonCartType = {
    products: ProductType[];
    address: AddressType;
    type: string;
    freight: number | null;
    totalDiscount: number | null;
};

type SellerCartData = {
    id: number;
    cpfCustomer: string;
    customerName: string;
    phoneCustomer: string;
    cpfSeller: string;
    pluginspaceId: number;
    locationId: number;
    jsonCart: JsonCartType;
    status: string;
    created: string;
    updated: string;
    canceled: string | null;
    urlcart: string;
    guid: string;
    order: string;
};

type SellerCartContextType = {
    data: SellerCartData;
    setSellerCartData: (cartData: SellerCartData) => void;
    updateJsonCart: (jsonCart: JsonCartType) => void;
    updateProducts: (products: ProductType[]) => void;
    updateAddress: (address: AddressType) => void;
    updateFreight: (freight: number) => void;
    updateTotalDiscount: (discount: number) => void;
    clearSellerCart: () => void;
    // Funções utilitárias para acessar dados específicos
    getSellerCartProducts: () => ProductType[];
    getSellerCartAddress: () => AddressType;
    getSellerCartFreight: () => number | null;
    getSellerCartTotalDiscount: () => number | null;
    isSellerCartValid: () => boolean;
};

const initialData: SellerCartData = {
    id: 0,
    cpfCustomer: "",
    customerName: "",
    phoneCustomer: "",
    cpfSeller: "",
    pluginspaceId: 0,
    locationId: 0,
    jsonCart: {
        products: [],
        address: {
            address: "",
            cep: "",
            city: "",
            complement: "",
            district: "",
            name: "",
            number: "",
            reference: "",
            telephone: "",
            state: "",
            ibge: "",
        },
        type: "",
        freight: null,
        totalDiscount: null,
    },
    status: "",
    created: "",
    updated: "",
    canceled: null,
    urlcart: "",
    guid: "",
    order: "",
};

const STORAGE_KEY = "sellerCartData";
const SECRET = !process.env.REACT_APP_JWT_SALT ? "ninegridCrypoToken" : process.env.REACT_APP_JWT_SALT;

export const SellerCartContext = createContext<SellerCartContextType | undefined>(undefined);

export const SellerCartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [data, setData] = useState<SellerCartData>(initialData);

    // Carrega do localStorage ao iniciar
    useEffect(() => {
        const encrypted = localStorage.getItem(STORAGE_KEY);
        if (encrypted) {
            try {
                const bytes = CryptoJS.AES.decrypt(encrypted, SECRET);
                const decrypted = bytes.toString(CryptoJS.enc.Utf8);
                if (decrypted) setData(JSON.parse(decrypted));
            } catch {
                setData(initialData);
            }
        }
    }, []);

    // Salva no localStorage sempre que mudar
    useEffect(() => {
        const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), SECRET).toString();
        localStorage.setItem(STORAGE_KEY, encrypted);
    }, [data]);

    const setSellerCartData = (cartData: SellerCartData) => {
        setData(cartData);
    };

    const updateJsonCart = (jsonCart: JsonCartType) => {
        setData((prev) => ({ ...prev, jsonCart }));
    };

    const updateProducts = (products: ProductType[]) => {
        setData((prev) => ({
            ...prev,
            jsonCart: {
                ...prev.jsonCart,
                products,
            },
        }));
    };

    const updateAddress = (address: AddressType) => {
        setData((prev) => ({
            ...prev,
            jsonCart: {
                ...prev.jsonCart,
                address,
            },
        }));
    };

    const updateFreight = (freight: number) => {
        setData((prev) => ({
            ...prev,
            jsonCart: {
                ...prev.jsonCart,
                freight,
            },
        }));
    };

    const updateTotalDiscount = (discount: number) => {
        setData((prev) => ({
            ...prev,
            jsonCart: {
                ...prev.jsonCart,
                totalDiscount: discount,
            },
        }));
    };

    const clearSellerCart = () => {
        setData(initialData);
        localStorage.removeItem(STORAGE_KEY);
    };

    // Funções utilitárias para acessar dados específicos
    const getSellerCartProducts = () => data.jsonCart.products;
    const getSellerCartAddress = () => data.jsonCart.address;
    const getSellerCartFreight = () => data.jsonCart.freight;
    const getSellerCartTotalDiscount = () => data.jsonCart.totalDiscount;
    const isSellerCartValid = () => data.id > 0 && data.status === 'open';

    return (
        <SellerCartContext.Provider 
            value={{ 
                data, 
                setSellerCartData, 
                updateJsonCart, 
                updateProducts, 
                updateAddress, 
                updateFreight, 
                updateTotalDiscount, 
                clearSellerCart,
                getSellerCartProducts,
                getSellerCartAddress,
                getSellerCartFreight,
                getSellerCartTotalDiscount,
                isSellerCartValid
            }}
        >
            {children}
        </SellerCartContext.Provider>
    );
};

export const useSellerCart = () => {
    const context = useContext(SellerCartContext);
    if (context === undefined) {
        throw new Error('useSellerCart must be used within a SellerCartProvider');
    }
    return context;
};
